import { Button } from '@nexds/web'
import styled, { css, keyframes } from 'styled-components'

import { appZIndex } from '@/presentation/utils/zIndexMapper'

const fadeIn = keyframes`
  from {
    opacity: 0;
    filter: blur(2px);
    transform: translate3d(0, -50%, 0);
    visibility: hidden;
  }
  to {
    opacity: 1;
    filter: blur(0px);
    transform: translate3d(-10px, -50%, 0);
    visibility: visible;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
    filter: blur(0px);
    transform: translate3d(-10px, -50%, 0);
    visibility: visible;
  }
  to {
    opacity: 0;
    filter: blur(2px);
    transform: translate3d(0, -50%, 0);
    visibility: hidden;
  }
`

interface FeatureInfoContainerProps {
  show: boolean
}

export const FeatureInfoContainer = styled.div<FeatureInfoContainerProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  z-index: ${appZIndex.INFO_PANEL};

  max-height: 95%;
  overflow-y: hidden;

  padding-bottom: 10px;
  width: 346px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.secondaryD1};

  box-shadow: -2px 4px 6px 0px ${({ theme }) => `${theme.colors.secondary}80`};

  opacity: 0;

  ${({ show }) =>
    show
      ? css`
          animation: ${fadeIn} 0.3s forwards;
        `
      : css`
          animation: ${fadeOut} 0.1s forwards;
        `};

  @media (max-width: 430px) {
    right: -10px;
    width: 82%;
  }
`

export const Header = styled.div`
  & * {
    user-select: text;
  }
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondaryL1};
`

export const Title = styled.h1`
  ${({ theme }) => theme.fonts.infoMD};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.neutralL5};
`

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
`

export const Body = styled.div`
  & * {
    user-select: text;
  }
  padding: 24px;
  max-height: calc(95vh - 100px);
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    border-radius: 20px;
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 20px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.secondaryL2};
    border-radius: 20px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.secondaryL3};
    cursor: default;
  }
`

// Meteorology-tooltip
export const Content = styled.p`
  ${({ theme }) => theme.fonts.infoSM};
  color: ${({ theme }) => theme.colors.neutralL5};
  margin-bottom: 8px;
`

// Meteorology-legends
export const LegendsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const LegendContainer = styled.div`
  background-color: ${({ theme }) => theme?.colors?.secondary ?? '#000'};
  padding: 4px 12px;
  border-radius: 10px;
`

export const LegendTitle = styled.h1`
  ${({ theme }) => theme?.fonts?.titleSM ?? {}}
  padding: 8px 0;
  color: ${({ theme }) => theme?.colors?.neutralL4 ?? '#fff'};
`

export const LegendItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
`

export const LegendText = styled.p`
  ${({ theme }) => theme?.fonts?.infoLG ?? {}}
  margin-left: 12px;
  color: ${({ theme }) => theme?.colors?.neutralL4 ?? '#fff'};
  flex: 1;
`

interface LegendColorProps {
  color: string
}
export const LegendColor = styled.div<LegendColorProps>`
  width: 24px;
  height: 24px;
  background-color: ${({ color }) => color};
  border-radius: 2px;
`

export const LegendImage = styled.img``

export const LegendTextIcon = styled.p`
  ${({ theme }) => theme?.fonts?.infoLG ?? {}}
  color: ${({ theme }) => theme?.colors?.neutralL4 ?? '#fff'};
  min-width: 36px;
`

export const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme?.colors?.secondaryL1 ?? '#000'};
`

// Navaid
export const NavaidBody = styled.div`
  & * {
    user-select: text;
  }
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 14px;

  max-height: calc(95vh - 100px);
  overflow-y: auto;

  &::-webkit-scrollbar {
    border-radius: 20px;
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 20px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.secondaryL2};
    border-radius: 20px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.secondaryL3};
    cursor: default;
  }
`

export const NavaidPurposeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  /* O pill de finalidade ("Em rota"/"Terminal") é apenas informativo. O componente Pill do
     design system é um <button> e acende o fundo no hover/active; neutralizamos esse efeito
     para que não pareça clicável. */
  & button {
    cursor: default;

    &:not(:disabled):hover,
    &:not(:disabled):active {
      background-color: transparent;
    }
  }
`

export const NavaidRemarksValue = styled.div`
  ${({ theme }) => theme.fonts.textMD};
  color: ${({ theme }) => theme.colors.neutralL4};
  white-space: pre-wrap;
  word-break: break-word;
`

export const NavaidActions = styled.div`
  display: flex;
  flex-direction: column;
`

export const MorseRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`

export const MorseCell = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.colors.secondaryL1};
  border-radius: 4px;
  padding: 10px;

  justify-content: center;

  gap: 10px;
  flex: 1 0 0;
`

export const MorseDot = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme?.colors?.neutralL4 ?? '#fff'};
`

export const MorseDash = styled.span`
  width: 12px;
  height: 4px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme?.colors?.neutralL4 ?? '#fff'};
`

// Airspaces
export const Section = styled.div`
  margin-bottom: 16px;
`

export const Subtitle = styled.h2`
  ${({ theme }) => theme.fonts.labelMD};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.neutralL5};
  margin-bottom: 24px;
`

interface LabelProps {
  $display?: 'block' | 'inline'
}

export const Label = styled.div<LabelProps>`
  ${({ theme }) => theme.fonts.infoMD};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.neutralL5};
  display: ${({ $display }) => $display || 'inline'};
  overflow-wrap: anywhere;
  word-break: break-word;
`

export const Value = styled.div`
  ${({ theme }) => theme.fonts.textSM};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.neutralL5};
  margin-top: 4px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
`

export const Separator = styled.div`
  margin: 16px 0;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.secondaryL1};
`

// NOTAM
export const TabContainerCustom = styled.div`
  display: flex;
  justify-content: center;
  margin: -20px -24px 16px;
  height: 40px;

  .component-tabs > div {
    padding: 4px 18px;
    gap: 8px;
  }
`
export const MessageContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: ${({ theme }) => theme.colors.neutralL3};
  ${({ theme }) => theme.fonts.textMD};
`

export const NotamListContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`

export const NotamListHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: 12px;
`

export interface NotamItemContainerProps {
  $backgroundColor: string
}

export const NotamItemContainer = styled.div<NotamItemContainerProps>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
`
export interface HighlightOnMapButtonProps {
  buttonColor: string
}

export const HighlightOnMapButton = styled(Button)<HighlightOnMapButtonProps>`
  margin-top: 8px;
  border-radius: 10px;
  height: 40px;
  padding: 12px 10px;
  background-color: ${({ buttonColor }) => buttonColor ?? '#1e262a'};
  span {
    ${({ theme }) => theme.fonts.infoMD}
  }
  svg {
    width: 18px;
    height: 18px;
  }
`

// Fuel
export const FuelContainer = styled.div`
  height: min(765px, calc(95vh - 16px));
  display: flex;
  flex-direction: column;
`

export const FuelContent = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  padding: 16px 8px 16px 16px;
  gap: 12px;
  overflow: hidden;
`

export const FuelSectionTitle = styled.h2`
  ${({ theme }) => theme.fonts.infoLG};
  color: ${({ theme }) => theme.colors.neutralL5};
  padding-right: 16px;
`

export const FuelCardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding-right: 6px;

  &::-webkit-scrollbar {
    border-radius: 20px;
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 20px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.secondaryL1};
    border-radius: 20px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.secondaryL2};
    cursor: default;
  }
`

export const FuelFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 16px 16px;
  background-color: ${({ theme }) => theme.colors.secondaryD1};
`

// Obstacle
export const ObstacleBody = styled.div`
  & * {
    user-select: text;
  }
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 14px;
`

export const ObstacleField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const ObstacleFieldLabel = styled.div`
  ${({ theme }) => theme.fonts.labelMD};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.neutralL0};
`

export const ObstacleFieldValuePrimary = styled.div`
  ${({ theme }) => theme.fonts.titleLG};
  color: ${({ theme }) => theme.colors.neutralL5};
  word-break: break-word;
`

export const ObstacleFieldValueSecondary = styled.div`
  ${({ theme }) => theme.fonts.h5};
  color: ${({ theme }) => theme.colors.neutralL4};
  word-break: break-word;
`

export const ObstacleFieldValueUnit = styled.span`
  color: ${({ theme }) => theme.colors.neutralL4};
`

export const ObstacleFieldValueText = styled.div`
  ${({ theme }) => theme.fonts.h5};
  color: ${({ theme }) => theme.colors.neutralL4};
`

export const ObstacleCoordinatesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const ObstacleCopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryL1};
  }
`
